import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    confirmAction,
    showErrorAlert,
    showSuccessToast,
    showValidationAlert,
} from '../catalog/catalogAlerts';
import {
    EmailTemplateFormState,
    EmailTemplateRecord,
    EmailTemplateType,
    emptyTemplateForm,
    extractApiErrorMessage,
    extractTemplateCollection,
    TEMPLATE_META,
    TemplateMeta,
    templateToFormState,
    validateTemplateForm,
} from './emailTemplatesApi';

import { getStoredToken } from '../../utils/authStorage';

const BASE_URL = (import.meta.env.VITE_API_BASE as string || 'http://localhost:8000').replace(/\/$/, '');

const apiGet = async (path: string) => {
    const token = getStoredToken();
    const res = await fetch(`${BASE_URL}/api${path}`, {
        headers: {
            Accept: 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
    });
    if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw Object.assign(new Error(body?.message ?? res.statusText), { response: { data: body } });
    }
    return res.json();
};

const apiPost = async (path: string, payload: unknown) => {
    const token = getStoredToken();
    const res = await fetch(`${BASE_URL}/api${path}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
    });
    if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw Object.assign(new Error(body?.message ?? res.statusText), { response: { data: body } });
    }
    return res.json();
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function StatusBadge({ configured }: { configured: boolean }) {
    return (
        <span
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${
                configured
                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                    : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
            }`}
        >
            <span
                className={`h-1.5 w-1.5 rounded-full ${
                    configured ? 'bg-emerald-500' : 'bg-amber-500'
                }`}
            />
            {configured ? 'Configured' : 'Not configured'}
        </span>
    );
}

interface TemplateCardProps {
    meta: TemplateMeta;
    record: EmailTemplateRecord | undefined;
    onClick: () => void;
}

function TemplateCard({ meta, record, onClick }: TemplateCardProps) {
    const configured = Boolean(record?.subject);

    return (
        <button
            id={`email-template-card-${meta.type}`}
            type="button"
            onClick={onClick}
            className="group relative flex w-full flex-col gap-3 rounded-2xl border border-gray-200 bg-white p-5 text-left transition-all hover:border-brand-300 hover:shadow-md hover:shadow-brand-100 dark:border-gray-800 dark:bg-gray-900 dark:hover:border-brand-700 dark:hover:shadow-brand-900/20"
        >
            {/* Icon + label */}
            <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-2xl dark:bg-brand-900/30">
                        {meta.icon}
                    </span>
                    <div>
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                            {meta.label}
                        </h3>
                        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
                            {meta.description}
                        </p>
                    </div>
                </div>
                <StatusBadge configured={configured} />
            </div>

            {/* Preview */}
            {record?.subject && (
                <div className="rounded-lg border border-gray-100 bg-gray-50/80 px-3 py-2 dark:border-gray-800 dark:bg-gray-800/50">
                    <p className="truncate text-xs font-medium text-gray-600 dark:text-gray-300">
                        {record.subject}
                    </p>
                </div>
            )}

            {/* Arrow */}
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-300 transition-all group-hover:text-brand-500 group-hover:translate-x-0.5 dark:text-gray-700">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M6 3L11 8L6 13" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </span>
        </button>
    );
}

// ---------------------------------------------------------------------------
// Variable Reference Panel
// ---------------------------------------------------------------------------

function VariableReference({
    meta,
    onInsert,
}: {
    meta: TemplateMeta;
    onInsert?: (key: string) => void;
}) {
    return (
        <div className="rounded-xl border border-dashed border-brand-200 bg-brand-50/40 p-4 dark:border-brand-800/60 dark:bg-brand-950/20">
            <h4 className="mb-2.5 text-xs font-semibold uppercase tracking-wider text-brand-600 dark:text-brand-400">
                Available Variables
            </h4>
            <div className="flex flex-col gap-1.5">
                {meta.variables.map((v) => (
                    <div key={v.key} className="flex items-start justify-between gap-2">
                        <button
                            type="button"
                            title="Click to copy"
                            onClick={() => {
                                void navigator.clipboard.writeText(v.key).catch(() => null);
                                onInsert?.(v.key);
                            }}
                            className="shrink-0 rounded bg-brand-100 px-1.5 py-0.5 font-mono text-xs text-brand-700 transition hover:bg-brand-200 dark:bg-brand-900/40 dark:text-brand-300 dark:hover:bg-brand-900/70"
                        >
                            {v.key}
                        </button>
                        <span className="text-right text-xs text-gray-500 dark:text-gray-400">
                            {v.description}
                        </span>
                    </div>
                ))}
            </div>
            <p className="mt-2.5 text-xs text-gray-400 dark:text-gray-500">
                Click a variable to copy it to your clipboard.
            </p>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Editor Panel (slide-over)
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Email Preview Modal
// ---------------------------------------------------------------------------

function EmailPreviewModal({
    html,
    subject,
    onClose,
}: {
    html: string;
    subject: string;
    onClose: () => void;
}) {
    const [device, setDevice] = useState<'desktop' | 'mobile'>('desktop');
    const iframeRef = useRef<HTMLIFrameElement>(null);

    // Close on Escape
    useEffect(() => {
        const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [onClose]);

    // Write HTML into iframe after mount
    useEffect(() => {
        const iframe = iframeRef.current;
        if (!iframe) return;
        const doc = iframe.contentDocument;
        if (!doc) return;
        doc.open();
        doc.write(html || '<p style="font-family:sans-serif;color:#6b7280;padding:32px">No email body to preview.</p>');
        doc.close();
    }, [html, device]);

    const modal = (
        <div
            className="fixed inset-0 z-[9999] flex flex-col bg-gray-950/80 backdrop-blur-sm"
            role="dialog"
            aria-modal="true"
            aria-label="Email preview"
        >
            {/* Toolbar */}
            <div className="flex shrink-0 items-center justify-between gap-4 border-b border-gray-800 bg-gray-900 px-6 py-3">
                <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold text-white">Preview</span>
                    {subject && (
                        <span className="max-w-xs truncate rounded-md bg-gray-800 px-3 py-1 text-xs text-gray-300">
                            Subject: {subject}
                        </span>
                    )}
                </div>

                {/* Device toggle */}
                <div className="flex items-center gap-1 rounded-lg border border-gray-700 bg-gray-800 p-1">
                    <button
                        type="button"
                        onClick={() => setDevice('desktop')}
                        title="Desktop view"
                        className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                            device === 'desktop'
                                ? 'bg-brand-600 text-white'
                                : 'text-gray-400 hover:text-white'
                        }`}
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="2" y="3" width="20" height="14" rx="2"/>
                            <path d="M8 21h8M12 17v4"/>
                        </svg>
                        Desktop
                    </button>
                    <button
                        type="button"
                        onClick={() => setDevice('mobile')}
                        title="Mobile view"
                        className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                            device === 'mobile'
                                ? 'bg-brand-600 text-white'
                                : 'text-gray-400 hover:text-white'
                        }`}
                    >
                        <svg width="12" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="5" y="2" width="14" height="20" rx="2"/>
                            <circle cx="12" cy="18" r="1"/>
                        </svg>
                        Mobile
                    </button>
                </div>

                <button
                    type="button"
                    onClick={onClose}
                    className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition hover:bg-gray-800 hover:text-white"
                    aria-label="Close preview"
                >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"/>
                    </svg>
                </button>
            </div>

            {/* Preview area */}
            <div className="flex flex-1 items-start justify-center overflow-y-auto bg-gray-950 py-8">
                <div
                    className="transition-all duration-300 rounded-xl overflow-hidden shadow-2xl"
                    style={{ width: device === 'desktop' ? '760px' : '390px', background: '#fff' }}
                >
                    <iframe
                        ref={iframeRef}
                        sandbox="allow-same-origin"
                        title="Email preview"
                        style={{
                            width: '100%',
                            height: '800px',
                            border: 'none',
                            display: 'block',
                        }}
                    />
                </div>
            </div>
        </div>
    );

    return createPortal(modal, document.body);
}

// ---------------------------------------------------------------------------

interface EditorPanelProps {
    meta: TemplateMeta;
    form: EmailTemplateFormState;
    submitting: boolean;
    onFieldChange: (field: 'subject' | 'body', value: string) => void;
    onSubmit: (event: FormEvent) => void;
    onClose: () => void;
}

function EditorPanel({
    meta,
    form,
    submitting,
    onFieldChange,
    onSubmit,
    onClose,
}: EditorPanelProps) {
    const [previewOpen, setPreviewOpen] = useState(false);
    const inputClassName =
        'w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-800 outline-none transition-all focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:bg-gray-800 dark:text-white';

    return (
        <div
            id={`email-template-editor-${meta.type}`}
            className="flex flex-col h-full"
        >
            {/* Header */}
            <div className="flex items-center justify-between gap-3 border-b border-gray-200 px-6 py-4 dark:border-gray-800 shrink-0">
                <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-xl dark:bg-brand-900/30">
                        {meta.icon}
                    </span>
                    <div>
                        <h2 className="text-base font-bold text-gray-900 dark:text-white">
                            {meta.label}
                        </h2>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            {meta.description}
                        </p>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={onClose}
                    className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800 dark:hover:text-gray-200"
                    aria-label="Close editor"
                >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
                    </svg>
                </button>
            </div>

            {/* Scrollable body */}
            <form onSubmit={onSubmit} className="flex flex-1 min-h-0 flex-col">
                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
                    {/* Subject */}
                    <div>
                        <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Subject Line <span className="text-red-500">*</span>
                        </label>
                        <input
                            id={`email-template-subject-${meta.type}`}
                            type="text"
                            value={form.subject}
                            onChange={(e) => onFieldChange('subject', e.target.value)}
                            className={inputClassName}
                            placeholder={`e.g. Welcome to {{store_name}}!`}
                            maxLength={255}
                            required
                            disabled={submitting}
                        />
                        <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                            You may use template variables in the subject line.
                        </p>
                    </div>

                    {/* WYSIWYG Body */}
                    <div>
                        <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Email Body <span className="text-red-500">*</span>
                        </label>
                        <textarea
                            id={`email-template-body-${meta.type}`}
                            value={form.body}
                            onChange={(e) => onFieldChange('body', e.target.value)}
                            placeholder="<html><body>...</body></html>"
                            disabled={submitting}
                            className="w-full rounded-xl border border-gray-200 bg-gray-50 p-4 font-mono text-sm leading-relaxed text-gray-800 outline-none transition-all focus:border-brand-500 focus:bg-white focus:ring-2 focus:ring-brand-500/20 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:bg-gray-900/50 dark:text-gray-300 dark:focus:bg-gray-900"
                            style={{ minHeight: '320px', resize: 'vertical' }}
                            spellCheck={false}
                        />
                    </div>

                    {/* Variable reference */}
                    <VariableReference meta={meta} />
                </div>

                {/* Footer */}
                <div className="shrink-0 border-t border-gray-200 bg-white px-6 py-4 dark:border-gray-800 dark:bg-gray-900">
                    <div className="flex items-center justify-between gap-3">
                        {/* Left: Preview */}
                        <button
                            id={`email-template-preview-${meta.type}`}
                            type="button"
                            onClick={() => setPreviewOpen(true)}
                            disabled={submitting || !form.body}
                            className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:border-brand-700 dark:hover:bg-brand-900/20 dark:hover:text-brand-300"
                        >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                <circle cx="12" cy="12" r="3"/>
                            </svg>
                            Preview
                        </button>

                        {/* Right: Cancel + Save */}
                        <div className="flex items-center gap-3">
                            <button
                                type="button"
                                onClick={onClose}
                                disabled={submitting}
                                className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200 disabled:opacity-60 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                            >
                                Cancel
                            </button>
                            <button
                                id={`email-template-save-${meta.type}`}
                                type="submit"
                                disabled={submitting}
                                className="rounded-lg bg-brand-500 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                {submitting ? (
                                    <span className="flex items-center gap-2">
                                        <svg className="h-3.5 w-3.5 animate-spin" viewBox="0 0 24 24" fill="none">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                                        </svg>
                                        Saving…
                                    </span>
                                ) : 'Save Template'}
                            </button>
                        </div>
                    </div>
                </div>
            </form>

            {/* Preview Modal (portal) */}
            {previewOpen && (
                <EmailPreviewModal
                    html={form.body}
                    subject={form.subject}
                    onClose={() => setPreviewOpen(false)}
                />
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function CommunicationsEmailTemplates() {
    const [templates, setTemplates] = useState<EmailTemplateRecord[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedType, setSelectedType] = useState<EmailTemplateType | null>(null);
    const [form, setForm] = useState<EmailTemplateFormState | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const panelRef = useRef<HTMLDivElement>(null);

    // Map type → saved record for O(1) lookup
    const recordByType = Object.fromEntries(
        templates.map((t) => [t.type, t]),
    ) as Record<string, EmailTemplateRecord | undefined>;

    const loadTemplates = useCallback(async () => {
        setLoading(true);
        try {
            const response = await apiGet('/communications/email-templates');
            setTemplates(extractTemplateCollection(response));
        } catch (err) {
            console.error('Failed to load email templates:', err);
            await showErrorAlert(extractApiErrorMessage(err, 'Failed to load email templates'));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void loadTemplates();
    }, [loadTemplates]);

    // Close editor panel on Escape key
    useEffect(() => {
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && selectedType) {
                handleClose();
            }
        };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedType]);

    const openEditor = (type: EmailTemplateType) => {
        const existing = recordByType[type];
        setForm(existing ? templateToFormState(existing) : emptyTemplateForm(type));
        setSelectedType(type);
        // Scroll panel into view on mobile
        setTimeout(() => panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    };

    const handleClose = async () => {
        const hasContent = form && (form.subject.trim() || form.body.replace(/<[^>]*>/g, '').trim());
        const originalRecord = selectedType ? recordByType[selectedType] : undefined;
        const isDirty =
            hasContent &&
            (form?.subject !== (originalRecord?.subject ?? '') ||
                form?.body !== (originalRecord?.body ?? ''));

        if (isDirty) {
            const confirmed = await confirmAction({
                text: 'You have unsaved changes. Are you sure you want to close the editor?',
                confirmButtonText: 'Discard Changes',
            });
            if (!confirmed) return;
        }

        setSelectedType(null);
        setForm(null);
    };

    const handleFieldChange = (field: 'subject' | 'body', value: string) => {
        setForm((prev) => (prev ? { ...prev, [field]: value } : null));
    };

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        if (!form) return;

        const validated = validateTemplateForm(form);
        if (!validated.valid) {
            await showValidationAlert(validated.message);
            return;
        }

        setSubmitting(true);
        try {
            await apiPost('/communications/email-template/upsert', validated.data);
            await loadTemplates();
            setSelectedType(null);
            setForm(null);
            showSuccessToast('Email template saved successfully!');
        } catch (err) {
            console.error('Failed to save email template:', err);
            await showErrorAlert(extractApiErrorMessage(err, 'Failed to save email template'));
        } finally {
            setSubmitting(false);
        }
    };

    const activeMeta = TEMPLATE_META.find((m) => m.type === selectedType) ?? null;

    const configuredCount = TEMPLATE_META.filter((m) => recordByType[m.type]?.subject).length;

    return (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
            {/* Header */}
            <div className="flex items-center justify-between gap-4 border-b border-gray-200 p-5 dark:border-gray-800">
                <div>
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
                        Email Templates
                    </h3>
                    <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
                        {loading
                            ? 'Loading templates…'
                            : `${configuredCount} of ${TEMPLATE_META.length} templates configured`}
                    </p>
                </div>
                {!loading && configuredCount < TEMPLATE_META.length && (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                            <path d="M6 1L1 10.5H11L6 1Z" stroke="currentColor" strokeWidth="1.25" strokeLinejoin="round" />
                            <path d="M6 5V7.5" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" />
                            <circle cx="6" cy="9" r="0.5" fill="currentColor" />
                        </svg>
                        {TEMPLATE_META.length - configuredCount} not yet configured
                    </span>
                )}
            </div>

            {/* Two-column layout: cards + editor */}
            <div className={`grid transition-all duration-300 ${selectedType ? 'lg:grid-cols-[1fr_520px]' : 'grid-cols-1'}`}>
                {/* Template Cards Grid */}
                <div className="p-5">
                    {loading ? (
                        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
                            {[...Array(5)].map((_, i) => (
                                <div
                                    key={i}
                                    className="h-24 animate-pulse rounded-2xl border border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-800/50"
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
                            {TEMPLATE_META.map((meta) => (
                                <TemplateCard
                                    key={meta.type}
                                    meta={meta}
                                    record={recordByType[meta.type]}
                                    onClick={() => openEditor(meta.type)}
                                />
                            ))}
                        </div>
                    )}
                </div>

                {/* Editor Panel */}
                {selectedType && activeMeta && form && (
                    <div
                        ref={panelRef}
                        className="border-t border-gray-200 lg:border-t-0 lg:border-l dark:border-gray-800 h-full min-h-[640px] flex flex-col"
                    >
                        <EditorPanel
                            meta={activeMeta}
                            form={form}
                            submitting={submitting}
                            onFieldChange={handleFieldChange}
                            onSubmit={handleSubmit}
                            onClose={() => void handleClose()}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
